import React, { useState, useEffect, useRef } from 'react'

const FocusRuler = () => {
  const [currentValue, setCurrentValue] = useState(50)
  const [isDragging, setIsDragging] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const lastMouseX = useRef(0)

  const tickWidth = 8
  const totalTicks = 100
  const sensitivity = 0.8
  const sideSpace = 400 // Padding on each side for visual purposes

  const getScrollForValue = (value: number, containerWidth: number) => {
    // Ensure value is clamped between 1 and 100
    const clampedValue = Math.min(Math.max(value, 1), 100)
    // Adjust calculation to align with tickmarks precisely
    return ((clampedValue - 1) * tickWidth) + sideSpace - (containerWidth / 2) + (tickWidth / 2)
  }

  const getValueFromScroll = (scrollLeft: number, containerWidth: number) => {
    // Adjust calculation to match the new scroll position logic
    const rawValue = Math.round((scrollLeft - sideSpace + (containerWidth / 2) - (tickWidth / 2)) / tickWidth) + 1
    return Math.min(Math.max(rawValue, 1), 100)
  }

  const constrainScroll = (newScrollLeft: number) => {
    if (!containerRef.current) return 0
    const containerWidth = containerRef.current.offsetWidth
    
    // Calculate exact positions for values 1 and 100
    const minScroll = getScrollForValue(1, containerWidth)
    const maxScroll = getScrollForValue(100, containerWidth)
    
    // Hard stop at boundaries
    if (newScrollLeft < minScroll) return minScroll
    if (newScrollLeft > maxScroll) return maxScroll
    
    return newScrollLeft
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true)
    lastMouseX.current = e.pageX
    e.preventDefault()
  }

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || !containerRef.current) return
    e.preventDefault()
    
    const delta = (lastMouseX.current - e.pageX) / sensitivity
    lastMouseX.current = e.pageX
    
    const newScrollLeft = containerRef.current.scrollLeft + delta
    containerRef.current.scrollLeft = constrainScroll(newScrollLeft)
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true)
    lastMouseX.current = e.touches[0].pageX
  }

  const handleTouchMove = (e: TouchEvent) => {
    if (!isDragging || !containerRef.current) return
    e.preventDefault()
    
    const delta = (lastMouseX.current - (e as TouchEvent).touches[0].pageX) / sensitivity
    lastMouseX.current = (e as TouchEvent).touches[0].pageX
    
    const newScrollLeft = containerRef.current.scrollLeft + delta
    containerRef.current.scrollLeft = constrainScroll(newScrollLeft)
  }

  const handleScroll = () => {
    if (!containerRef.current) return
    const containerWidth = containerRef.current.offsetWidth
    const scrollLeft = containerRef.current.scrollLeft
    
    // Enforce scroll boundaries immediately
    const constrainedScroll = constrainScroll(scrollLeft)
    if (constrainedScroll !== scrollLeft) {
      containerRef.current.scrollLeft = constrainedScroll
    }
    
    const newValue = getValueFromScroll(constrainedScroll, containerWidth)
    setCurrentValue(newValue)
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const handleTouchEnd = () => {
    setIsDragging(false)
  }

  useEffect(() => {
    const element = containerRef.current
    if (!element) return
    
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    document.addEventListener('touchmove', handleTouchMove, { passive: false })
    document.addEventListener('touchend', handleTouchEnd)

    // Set initial scroll position
    const containerWidth = element.offsetWidth
    const initialScroll = getScrollForValue(currentValue, containerWidth)
    element.scrollLeft = initialScroll

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', handleTouchEnd)
    }
  }, [isDragging])

  return (
    <>
      <div className="text-center mb-4 text-gray-600 font-medium">
        Ruler value: {currentValue}
      </div>
      
      {/* Ruler container with gradient mask */}
      <div className="relative bg-white rounded-lg pt-2 shadow-inner overflow-hidden">
        {/* Center indicator */}
        <div className="absolute left-1/2 top-0 -translate-x-1/2 z-20">
          <svg width="12" height="8" viewBox="0 0 12 8" className="fill-red-500">
            <path d="M6 8L0 0L12 0L6 8Z" />
          </svg>
        </div>
        
        <div 
          ref={containerRef}
          className={`overflow-x-scroll relative h-20 scrollbar-hide cursor-grab
            ${isDragging ? 'cursor-grabbing select-none' : ''}`}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          onScroll={handleScroll}
          style={{
            maskImage: 'linear-gradient(to right, transparent, black 20%, black 80%, transparent)',
            WebkitMaskImage: 'linear-gradient(to right, transparent, black 20%, black 80%, transparent)'
          }}
        >
          <div className="relative whitespace-nowrap pt-8" 
               style={{ 
                 width: `${(totalTicks * tickWidth) + (sideSpace * 2)}px`,
                 paddingLeft: `${sideSpace}px`,
                 paddingRight: `${sideSpace}px`
               }}>
            {Array.from({ length: totalTicks }, (_, i) => {
              const value = i + 1
              const isCurrentValue = value === currentValue
              return (
                <div 
                  key={value}
                  className="inline-block relative"
                  style={{ width: `${tickWidth}px` }}
                >
                  <div 
                    className={`absolute bottom-0 left-1/2 transform -translate-x-1/2
                      ${isCurrentValue 
                        ? 'bg-red-500' 
                        : value % 10 === 0 || value === 1 
                          ? 'bg-black' 
                          : value % 5 === 0 
                            ? 'bg-gray-700' 
                            : 'bg-gray-300'
                      } ${value % 10 === 0 || value === 1 ? 'h-10 w-1' : value % 5 === 0 ? 'h-6 w-px' : 'h-3 w-px'}`}
                  />
                  {(value % 10 === 0 || value === 1) && (
                    <div className={`absolute -bottom-7 left-0 w-full text-center text-sm font-medium
                      ${isCurrentValue ? 'text-red-500' : 'text-gray-700'}`}>
                      {value}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </>
  )
}

export default FocusRuler 